// src/modules/accounting/pages/ProfitLossPage.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Spin, Alert, Typography, Row, Col, DatePicker, Button, Space,
  Statistic, Tag, Empty, Tooltip, Divider
} from 'antd';
import {
  ReloadOutlined, ArrowUpOutlined, ArrowDownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getCategorisedProfitLoss, getProfitLossMonthlyTrend } from '../api';
import DataTable from '../../../components/UI/DataTable';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function ProfitLossPage() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].startOf('day').toISOString();
        params.endDate = dateRange[1].endOf('day').toISOString();
      }
      const [plRes, trendRes] = await Promise.all([
        getCategorisedProfitLoss(params),
        getProfitLossMonthlyTrend(params),
      ]);
      setData(plRes.data || plRes);
      setTrend((trendRes.data || trendRes).months || []);
    } catch (err) {
      setError(err.message || 'Failed to load P&L');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetch(); }, [fetch]);

  const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');
  const pct = (v) => v != null ? `${Number(v).toFixed(1)}%` : '—';

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />;
  if (error) return <Alert type="error" message={error} showIcon />;
  if (!data) return <Empty />;

  const renderItems = (items) => (
    <DataTable persistId="accounting.profit-loss.items" showToolbar={false}
      dataSource={(items || []).map((item, i) => ({
        code: item.account?.code,
        name: item.account?.name,
        balance: item.amount,
        key: i,
      }))}
      columns={[
        { title: 'Code', dataIndex: 'code', width: 80 },
        { title: 'Account', dataIndex: 'name' },
        { title: 'Amount', dataIndex: 'balance', render: (v) => `₹${fmt(v)}`, align: 'right', width: 160 },
      ]}
      pagination={false}
      size="small"
      showHeader={false}
    />
  );

  const netColor = (data.netProfit || 0) >= 0 ? '#3f8600' : '#cf1322';

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={5} style={{ margin: 0 }}>Profit & Loss Statement</Title></Col>
        <Col>
          <Space>
            <RangePicker value={dateRange} onChange={setDateRange} format="DD/MM/YYYY" allowClear />
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={fetch} /></Tooltip>
          </Space>
        </Col>
      </Row>

      {/* P&L Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small"><Statistic title="Total Revenue" value={fmt(data.revenue?.total)} prefix="₹" valueStyle={{ color: '#3f8600' }} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small"><Statistic title="Gross Profit" value={fmt(data.grossProfit)} prefix="₹"
            valueStyle={{ color: data.grossProfit >= 0 ? '#3f8600' : '#cf1322' }}
            suffix={<Text type="secondary" style={{ fontSize: 11 }}>{pct(data.grossProfitMargin)}</Text>}
          /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small"><Statistic title="Operating Profit" value={fmt(data.operatingProfit)} prefix="₹"
            valueStyle={{ color: data.operatingProfit >= 0 ? '#3f8600' : '#cf1322' }}
            suffix={<Text type="secondary" style={{ fontSize: 11 }}>{pct(data.operatingProfitMargin)}</Text>}
          /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small"><Statistic title="Net Profit" value={fmt(data.netProfit)}
            prefix={data.netProfit >= 0 ? <><ArrowUpOutlined /> ₹</> : <><ArrowDownOutlined /> ₹</>}
            valueStyle={{ color: netColor, fontWeight: 'bold' }}
            suffix={<Text type="secondary" style={{ fontSize: 11 }}>{pct(data.netProfitMargin)}</Text>}
          /></Card>
        </Col>
      </Row>

      {/* Revenue Section */}
      <Card title={<Tag color="green">REVENUE — ₹{fmt(data.revenue?.total)}</Tag>} size="small" style={{ marginBottom: 12 }}>
        {(data.revenue?.categories || []).map((cat) =>
          cat.items?.length > 0 && (
            <div key={cat.category} style={{ marginBottom: 8 }}>
              <Text strong style={{ fontSize: 12 }}>{cat.category} — ₹{fmt(cat.subtotal)}</Text>
              {renderItems(cat.items)}
            </div>
          )
        )}
      </Card>

      {/* Expense Section */}
      <Card title={<Tag color="red">EXPENSES — ₹{fmt(data.expenses?.total)}</Tag>} size="small" style={{ marginBottom: 12 }}>
        {(data.expenses?.categories || []).map((cat) =>
          cat.items?.length > 0 && (
            <div key={cat.category} style={{ marginBottom: 8 }}>
              <Text strong style={{ fontSize: 12 }}>{cat.category} — ₹{fmt(cat.subtotal)}</Text>
              {renderItems(cat.items)}
            </div>
          )
        )}
      </Card>

      {/* Bottom Line — Schedule III order */}
      <Card size="small" style={{ background: '#fafafa' }}>
        <Row justify="end" gutter={32}>
          {data.scheduleIII && (
            <>
              <Col><Statistic title="Total Income" value={fmt(data.scheduleIII.totalIncome)} prefix="₹" /></Col>
              <Col><Statistic title="Profit Before Tax" value={fmt(data.scheduleIII.profitBeforeTax)} prefix="₹" /></Col>
              <Col><Statistic title="Income Tax" value={fmt(data.scheduleIII.incomeTaxExpense)} prefix="₹" /></Col>
            </>
          )}
          <Col><Statistic title="Gross Profit" value={fmt(data.grossProfit)} prefix="₹" /></Col>
          <Col><Statistic title="Operating Profit" value={fmt(data.operatingProfit)} prefix="₹" /></Col>
          <Col><Statistic title={data.scheduleIII ? 'Net Profit (PAT)' : 'Net Profit'} value={fmt(data.netProfit)} prefix="₹" valueStyle={{ fontWeight: 'bold', color: netColor }} /></Col>
        </Row>
      </Card>

      {/* Unclassified accounts warning */}
      {data.scheduleIII?.unclassified?.accountCount > 0 && (
        <Alert
          style={{ marginTop: 12 }}
          type="warning"
          showIcon
          message={`${data.scheduleIII.unclassified.accountCount} P&L account(s) have no nature assigned`}
          description="Their amounts appear under 'Unclassified' above. Assign natures in Chart of Accounts so they land on the correct statement line."
        />
      )}

      {/* Monthly Trend */}
      {trend.length > 0 && (
        <>
          <Divider />
          <Title level={5}>Monthly Trend</Title>
          <DataTable persistId="accounting.profit-loss.monthly"
            dataSource={trend.map((m, i) => ({ ...m, key: i }))}
            columns={[
              { title: 'Month', dataIndex: 'monthLabel', key: 'month' },
              { title: 'Revenue', dataIndex: 'revenue', render: (v) => `₹${fmt(v)}`, align: 'right' },
              { title: 'Expenses', dataIndex: 'expenses', render: (v) => `₹${fmt(v)}`, align: 'right' },
              { title: 'Net Profit', dataIndex: 'netProfit', render: (v) => <Text style={{ color: v >= 0 ? '#3f8600' : '#cf1322' }}>₹{fmt(v)}</Text>, align: 'right' },
              { title: 'Margin', dataIndex: 'netProfitMargin', render: (v) => pct(v), align: 'right' },
            ]}
            pagination={false}
            size="small"
          />
        </>
      )}
    </div>
  );
}
